import {
  extractMemoryAssetsFromGamesPayload,
  formatTime,
} from "../memory-match/gameUtils";

export { formatTime };

// ---------------------------------------------------------------------------
// Shared bubble kinds. Ported 1:1 from the iOS games:
//  • Bubble Pop (BubblePopGameView.swift) — the endless colour-chase popper.
//  • Bubble Pop: Math (BubblePopMathGameView.swift) — the number chase.
// ---------------------------------------------------------------------------

export const BUBBLE_KINDS = {
  NORMAL: "normal",
  RAINBOW: "rainbow",
  BOMB: "bomb",
};

export const BASE_POINTS = {
  [BUBBLE_KINDS.NORMAL]: 10,
  [BUBBLE_KINDS.RAINBOW]: 20,
  [BUBBLE_KINDS.BOMB]: 0,
};

export const isCharacterKind = (kind) => kind === BUBBLE_KINDS.NORMAL;

const randomInteger = (min, max, random = Math.random) =>
  Math.floor(random() * (max - min + 1)) + min;

const randomBetween = (min, max, random = Math.random) =>
  min + random() * (max - min);

export const extractBubbleAssetsFromGamesPayload = (payload) =>
  extractMemoryAssetsFromGamesPayload(payload);

// ---------------------------------------------------------------------------
// Bubble Pop — the colour chase
// ---------------------------------------------------------------------------

// The bubble colours the game is played with. One is the current "magic
// colour" the child must pop; the rest are decoys. Hex values match the iOS
// BubbleHue base/deep colours.
export const BUBBLE_HUES = [
  { name: "blue", label: "BLUE", base: "#408FFF", deep: "#0061E0" },
  { name: "pink", label: "PINK", base: "#FC73B8", deep: "#E03D8C" },
  { name: "green", label: "GREEN", base: "#45CC73", deep: "#1A9E52" },
  { name: "orange", label: "ORANGE", base: "#FF9E33", deep: "#E6730D" },
  { name: "purple", label: "PURPLE", base: "#9E6BF5", deep: "#7340D9" },
];

export const hueByName = (name) =>
  BUBBLE_HUES.find((hue) => hue.name === name) || BUBBLE_HUES[0];

export const MAX_LIVES = 3;
export const COMBO_WINDOW_SECONDS = 1.4;
export const MAX_COMBO_MULTIPLIER = 10;
export const FEVER_EVERY = 12; // combo size that triggers fever
export const FEVER_DURATION_SECONDS = 6;
export const GOLD_RUSH_EVERY_SECONDS = 30; // seconds survived between rushes
export const GOLD_RUSH_DURATION_SECONDS = 5;
export const HUE_SWITCH_MIN_SECONDS = 10;
export const HUE_SWITCH_MAX_SECONDS = 15;
export const HUE_WAVE_DURATION_SECONDS = 0.55;
// Covers the whole clearing wave plus human reaction time.
export const HUE_GRACE_SECONDS = 0.9;
export const NORMAL_SCREEN_CAP = 11;
export const REWARD_SCREEN_CAP = 16;
export const SUPER_CHARGE_PER_POP = 0.08;

// Reaction-time skill: how long recent magic-colour bubbles lived before they
// were popped. A bubble ignored while popping others isn't a slow reaction, so
// one sample can claim at most REACTION_CEILING seconds.
export const REACTION_SAMPLE_COUNT = 8;
export const REACTION_CEILING = 4.0;
const REACTION_FAST = 0.8;
const REACTION_SLOW = 2.4;

// 0 = struggling, 1 = popping on sight. Neutral until there are samples, so
// the opening of a round always plays the same.
export const reactionSkill = (samples) => {
  if (!samples || !samples.length) return 0.5;
  const average = samples.reduce((sum, value) => sum + value, 0) / samples.length;
  return Math.min(1, Math.max(0, (REACTION_SLOW - average) / (REACTION_SLOW - REACTION_FAST)));
};

// Difficulty curve. Time survived sets the baseline (0 → 1 over two minutes),
// then measured reaction time scales it: a child popping on sight climbs up to
// 30% faster; one who is struggling has the same curve stretched out.
export const difficultyRamp = (elapsedSeconds, reactionSamples = []) => {
  const timeRamp = Math.min(1, Math.max(0, elapsedSeconds) / 120);
  return Math.min(1, timeRamp * (0.7 + 0.6 * reactionSkill(reactionSamples)));
};

// Background "heats up" as the game speeds up.
export const intensityForElapsed = (elapsedSeconds) =>
  Math.min(1, Math.max(0, elapsedSeconds) / 65);

export const comboMultiplier = (combo) =>
  Math.max(1, Math.min(combo, MAX_COMBO_MULTIPLIER));

// Spawn accelerates the longer you survive; floods during rewards.
export const spawnIntervalForState = ({ ramp, isFever, isGoldRush }) => {
  let interval = Math.max(0.26, 0.7 - 0.72 * ramp);
  if (isFever) interval *= 0.7;
  if (isGoldRush) interval *= 0.6;
  return Math.max(0.15, interval);
};

// Double-spawns get more common the longer the run goes.
export const doubleSpawnChance = (ramp) => 0.25 + 0.15 * ramp;

// Decoy colour count: ONE at the start (a gentle two-colour game), another
// joining as the ramp climbs until all four are live.
export const activeDecoyCount = (ramp) => Math.min(4, 1 + Math.floor(ramp * 4.8));

// The share of spawns wearing the magic colour (60% → 40% over the ramp).
// Fever floods the magic colour; a bubble rush is ALL magic colour.
export const targetHueBias = ({ ramp, isFever, isGoldRush }) =>
  isGoldRush ? 1.0 : isFever ? 0.7 : 0.6 - 0.2 * ramp;

export const pickBubbleKind = ({ ramp, isFever, isGoldRush, random = Math.random }) => {
  if (isGoldRush) {
    // Bubble rush: a hazard-free flood — every bubble is poppable.
    return random() < 0.05 ? BUBBLE_KINDS.RAINBOW : BUBBLE_KINDS.NORMAL;
  }
  if (isFever) {
    // Fever floods bubbles; hazards pause so the flood stays fair.
    return random() < 0.035 ? BUBBLE_KINDS.RAINBOW : BUBBLE_KINDS.NORMAL;
  }
  // Bombs get more common as the round goes on.
  const bombProbability = Math.min(0.3, 0.08 + 0.48 * ramp);
  if (random() < bombProbability) return BUBBLE_KINDS.BOMB;
  return random() < 0.035 ? BUBBLE_KINDS.RAINBOW : BUBBLE_KINDS.NORMAL;
};

// Generous size floors so a bubble's colour is always readable at a glance.
const sizeRangeForKind = (kind) => {
  switch (kind) {
    case BUBBLE_KINDS.RAINBOW:
      return [92, 112];
    case BUBBLE_KINDS.BOMB:
      return [68, 84];
    default:
      return [80, 104];
  }
};

export const buildBubbleSpec = ({
  id,
  kind,
  hue,
  area,
  ramp,
  forgiving = false,
  random = Math.random,
}) => {
  const [minSize, maxSize] = sizeRangeForKind(kind);
  const size = Math.round(randomBetween(minSize, maxSize, random));
  const half = size / 2;
  const width = Math.max(size, area?.width || size);
  const height = Math.max(size, area?.height || size);
  // Bubbles get up to ~35% faster over the ramp — but never so fast that one
  // crosses the play field in under two seconds.
  const speedScale = 1 + 0.35 * ramp;
  const baseSpeed =
    kind === BUBBLE_KINDS.NORMAL
      ? randomBetween(115, 180, random)
      : randomBetween(150, 205, random);

  return {
    id,
    kind,
    hue,
    x: randomBetween(half, Math.max(half, width - half), random),
    y: height + half,
    size,
    speed: Math.min(baseSpeed * speedScale, height / 2),
    drift: randomBetween(-45, 45, random),
    wobblePhase: randomBetween(0, Math.PI * 2, random),
    forgiving,
    age: 0,
  };
};

// Fixed-step movement shared by both games. Escape (past the TOP) only counts
// against magic-colour character bubbles that aren't forgiving.
export const advanceBubbles = (bubbles, dt, area, targetHueName = null) => {
  const next = [];
  let escapedCharacters = 0;

  for (const bubble of bubbles) {
    const moved = { ...bubble };
    moved.age += dt;
    moved.y -= moved.speed * dt;
    const sway = Math.sin(moved.age * 2.2 + moved.wobblePhase) * 18 * dt;
    moved.x += moved.drift * dt + sway;

    const half = moved.size / 2;
    if (moved.x < half) {
      moved.x = half;
      moved.drift = Math.abs(moved.drift);
    }
    if (moved.x > area.width - half) {
      moved.x = area.width - half;
      moved.drift = -Math.abs(moved.drift);
    }

    if (moved.y + half > 0) {
      next.push(moved);
    } else if (
      moved.kind === BUBBLE_KINDS.NORMAL &&
      (targetHueName === null || moved.hue?.name === targetHueName) &&
      !moved.forgiving
    ) {
      escapedCharacters += 1; // a magic-colour bubble got away
    }
  }

  return { bubbles: next, escapedCharacters };
};

// The colour-change wave: smoothstep-eased front sweeping down the screen.
export const hueWaveFrontY = (progress, areaHeight) => {
  const clamped = Math.min(1, Math.max(0, progress));
  const eased = clamped * clamped * (3 - 2 * clamped);
  // Runs past the bottom edge (+130) so just-spawned stragglers are swept too.
  return (areaHeight + 130) * eased;
};

export const pointsForBubble = ({ kind, combo, isFever }) =>
  (BASE_POINTS[kind] || 0) * comboMultiplier(combo) * (isFever ? 2 : 1);

export const comboMilestone = (combo) => {
  switch (combo) {
    case 5:
      return `NICE!  ×${comboMultiplier(combo)}`;
    case 10:
      return `SUPER!  ×${comboMultiplier(combo)}`;
    case 15:
      return `AMAZING!  ×${comboMultiplier(combo)}`;
    case 20:
      return "INCREDIBLE!";
    default:
      return combo > 20 && combo % 10 === 0 ? "UNSTOPPABLE!" : null;
  }
};

// The magic-colour switch clock: switches arrive faster the longer the run
// (down to ~65% of the gap).
export const nextHueSwitchDelay = (ramp, random = Math.random) =>
  randomBetween(HUE_SWITCH_MIN_SECONDS, HUE_SWITCH_MAX_SECONDS, random) * (1 - 0.35 * ramp);

export const bubblePopResultTitle = (reason) => {
  switch (reason) {
    case "bomb":
      return "Boom!";
    case "missed":
      return "They floated away!";
    case "wrongColor":
      return "Wrong color!";
    default:
      return "Great popping!";
  }
};

// ---------------------------------------------------------------------------
// Bubble Pop: Math — the number chase
// ---------------------------------------------------------------------------

// The arithmetic skills the game teaches, in unlock order.
export const MATH_SKILLS = {
  ADDITION: "addition",
  SUBTRACTION: "subtraction",
  MULTIPLICATION: "multiplication",
  DIVISION: "division",
};

export const MATH_MAX_LIVES = 3;
export const MATH_POPS_PER_LEVEL = 6;
export const MATH_MAX_MULTIPLIER = 5;
// Every Nth bubble is the rainbow helper.
export const MATH_RAINBOW_EVERY = 11;
// A round that never gets its matches (unkind spawns) rolls over for free.
export const MATH_ROUND_TIMEOUT_SECONDS = 22;
export const MATH_ROUND_CLEAR_BONUS = 15;
export const MATH_LEVEL_UP_BONUS = 25;

export const mathSkillsForLevel = (level) => {
  const unlocked = [MATH_SKILLS.ADDITION];
  if (level >= 2) unlocked.push(MATH_SKILLS.SUBTRACTION);
  if (level >= 4) unlocked.push(MATH_SKILLS.MULTIPLICATION);
  if (level >= 5) unlocked.push(MATH_SKILLS.DIVISION);
  return unlocked;
};

// Level-up banner copy: { title, subtitle }.
export const mathUnlockCopy = (level) => {
  switch (level) {
    case 2:
      return { title: "TAKE AWAY!", subtitle: "Subtraction joins in −" };
    case 3:
      return { title: "BIGGER NUMBERS!", subtitle: "Counting up to 20" };
    case 4:
      return { title: "TIMES TABLES!", subtitle: "Multiplication joins in ×" };
    case 5:
      return { title: "FAIR SHARES!", subtitle: "Division joins in ÷" };
    default:
      return { title: `LEVEL ${level}!`, subtitle: "More bubbles, faster water" };
  }
};

// The number range the level plays inside.
export const mathCapForLevel = (level) => (level >= 3 ? 20 : 10);

// The next round's number. Addition is always unlocked and every target is
// ≥ 2, so makeMathProblem can always build for whatever comes back.
export const makeMathTarget = ({ level = 1, avoiding = null, random = Math.random } = {}) => {
  const upper = level >= 3 ? 15 : 9;
  for (let attempt = 0; attempt < 12; attempt += 1) {
    const target = randomInteger(2, upper, random);
    if (target !== avoiding) return target;
  }
  return randomInteger(2, upper, random);
};

const buildMathSkillProblem = (skill, answer, level, random) => {
  const cap = mathCapForLevel(level);
  switch (skill) {
    case MATH_SKILLS.ADDITION: {
      if (answer < 2) return null;
      const a = randomInteger(1, answer - 1, random);
      return { text: `${a}+${answer - a}`, answer };
    }
    case MATH_SKILLS.SUBTRACTION: {
      if (answer >= cap) return null;
      const b = randomInteger(1, cap - answer, random);
      return { text: `${answer + b}−${b}`, answer };
    }
    case MATH_SKILLS.MULTIPLICATION: {
      // Only the small tables, and never the giveaway "n×1".
      const factors = [2, 3, 4, 5].filter(
        (f) => answer % f === 0 && answer / f <= 10 && answer / f > 1
      );
      if (!factors.length) return null;
      const f = factors[randomInteger(0, factors.length - 1, random)];
      return { text: `${f}×${answer / f}`, answer };
    }
    case MATH_SKILLS.DIVISION: {
      if (answer < 2 || answer > 10) return null;
      const divisors = [2, 3, 5].filter((d) => answer * d <= 40);
      if (!divisors.length) return null;
      const d = divisors[randomInteger(0, divisors.length - 1, random)];
      return { text: `${answer * d}÷${d}`, answer };
    }
    default:
      return null;
  }
};

const shuffled = (items, random) => {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
};

// A problem that makes EXACTLY `answer`, using a skill this level has
// unlocked. Skills that can't reach the number fall through to ones that can.
export const makeMathProblem = ({ answer, level = 1, random = Math.random }) => {
  const unlocked = mathSkillsForLevel(level);
  let pool = shuffled(unlocked, random);
  // Bias toward the newest unlock so a fresh skill shows up right away.
  if (unlocked.length > 1 && random() < 0.4) {
    pool = [unlocked[unlocked.length - 1], ...pool];
  }
  for (const skill of pool) {
    const problem = buildMathSkillProblem(skill, answer, level, random);
    if (problem) return problem;
  }
  return null;
};

// A decoy: any number except the target. Once the sums get real (level 3+)
// decoys start landing one or two away from the target, so the sum has to be
// actually read.
export const makeMathDecoy = ({ target, level = 1, tricky = false, random = Math.random }) => {
  const cap = mathCapForLevel(level);
  for (let attempt = 0; attempt < 16; attempt += 1) {
    let wrong;
    if (tricky && attempt < 8 && random() < 0.5) {
      const deltas = [-2, -1, 1, 2];
      const delta = deltas[randomInteger(0, deltas.length - 1, random)];
      wrong = Math.min(cap, Math.max(2, target + delta));
    } else {
      wrong = randomInteger(2, cap, random);
    }
    if (wrong === target) continue;
    const problem = makeMathProblem({ answer: wrong, level, random });
    if (problem) return problem;
  }
  // Belt and braces: a sum that provably isn't the target.
  const safe = target === 2 ? 3 : 2;
  return { text: `1+${safe - 1}`, answer: safe };
};

// Matches needed to clear a round — a short, always-winnable errand.
export const mathPopsNeeded = (level) => Math.min(6, 3 + Math.floor(level / 2));

// How many bubbles share the water at once — the core difficulty dial.
export const mathTargetOnScreen = (level) => {
  if (level <= 1) return 3;
  if (level === 2) return 4;
  if (level <= 4) return 5;
  return 6;
};

// Seconds a bubble takes to reach the surface: leisurely at level 1, urgent
// (but never frantic) later on.
export const mathCrossDurationSeconds = (level, random = Math.random) =>
  Math.max(7.5, randomBetween(13, 16, random) - (level - 1) * 0.7);

export const mathSpawnGap = (level) => Math.max(0.32, 0.7 - (level - 1) * 0.05);

// The share of spawns that make the number. It thins out as levels climb, so
// later rounds are more hunting and less harvesting.
export const mathTargetShare = (level) => Math.max(0.32, 0.5 - (level - 1) * 0.03);

export const mathMultiplier = (streak) =>
  Math.max(1, Math.min(streak, MATH_MAX_MULTIPLIER));

export const mathLevelForPopped = (poppedCount) =>
  Math.floor(Math.max(0, poppedCount) / MATH_POPS_PER_LEVEL) + 1;

export const mathStreakMilestone = (streak) => {
  switch (streak) {
    case 3:
      return `NICE!  ×${mathMultiplier(streak)}`;
    case 5:
      return "ON FIRE!";
    case 8:
      return "BRILLIANT!";
    case 12:
      return "GENIUS!";
    default:
      return streak > 12 && streak % 5 === 0 ? "UNSTOPPABLE!" : null;
  }
};

export const mathResultTitle = (poppedCount) => {
  if (poppedCount < 6) return "Good try!";
  if (poppedCount < 18) return "Great counting!";
  if (poppedCount < 36) return "Math star!";
  return "Math genius!";
};

// Problem text needs room; rainbows can be a touch bigger for drama. Bubbles
// shrink proportionally on short fields but never below 80%, and longer
// problems ride BIGGER bubbles so the text grows instead of squeezing.
export const mathBubbleSize = ({ problem, fieldHeight, random = Math.random }) => {
  const sizeScale = Math.min(1.0, Math.max(0.8, fieldHeight / 480));
  let base;
  if (!problem) {
    base = randomBetween(106, 118, random);
  } else if (problem.text.length <= 4) {
    base = randomBetween(100, 112, random);
  } else if (problem.text.length === 5) {
    base = randomBetween(110, 120, random);
  } else {
    base = randomBetween(118, 130, random);
  }
  return Math.round(base * sizeScale);
};

// Movement for the math game: bubbles rise toward the waterline (surfaceY) and
// burst when they touch it. Returns the survivors plus every surfaced bubble
// so the caller can judge misses and draw ripples.
export const advanceMathBalloons = (balloons, dt, area, surfaceY) => {
  const next = [];
  const surfaced = [];

  for (const balloon of balloons) {
    const moved = { ...balloon };
    moved.age += dt;
    moved.y -= moved.speed * dt;
    const sway = Math.sin(moved.age * 1.8 + moved.wobblePhase) * 14 * dt;
    moved.x += moved.drift * dt + sway;

    const half = moved.size / 2;
    if (moved.x < half) {
      moved.x = half;
      moved.drift = Math.abs(moved.drift);
    }
    if (moved.x > area.width - half) {
      moved.x = area.width - half;
      moved.drift = -Math.abs(moved.drift);
    }

    if (moved.y - half > surfaceY) {
      next.push(moved);
    } else {
      surfaced.push(moved);
    }
  }

  return { balloons: next, surfaced };
};

// Nearest-bubble hit test shared by both games (matches the iOS 0.58 radius).
export const hitTestBubble = (bubbles, point) => {
  let bestId = null;
  let bestDist = Number.POSITIVE_INFINITY;
  for (const bubble of bubbles) {
    const dx = bubble.x - point.x;
    const dy = bubble.y - point.y;
    const d = Math.sqrt(dx * dx + dy * dy);
    if (d <= bubble.size * 0.58 && d < bestDist) {
      bestDist = d;
      bestId = bubble.id;
    }
  }
  return bestId;
};
